/**
 * VendorDirectoryPanel — owner-phase HomeHQ feature. Lets the homeowner
 * curate 2–3 vetted service providers per trade (plumber, electrician,
 * HVAC, etc.) so they have someone to call BEFORE issues arise — like
 * choosing in-network doctors when you switch health insurance.
 *
 * First screen renders the saved/pinned vendors, grouped by trade. Empty
 * trades show inline CTAs to find vendors (the discovery flow lives in a
 * follow-up screen — placeholder for now).
 *
 * Backed by mocked data via `useVendors` — see
 * `services/api/vendors/api.ts` (USE_MOCKS toggle).
 */
import { CircularProgress, Dialog as MuiDialog } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog } from '@/components/common/Dialog';
import { CloseButton, SortCaret } from '@/components/ds';
import { VendorFinderDialog } from '@/components/home/VendorFinderDialog';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { useGetIssuesForProperty } from '@/hooks/issues/useGetIssuesForProperty';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useUserRole } from '@/hooks/useUserRole';
import { useVendors } from '@/hooks/useVendors';
import { Issue, IssueUrgency } from '@/models/Issue';
import {
  VENDOR_TRADE_GROUPS,
  VENDOR_TRADE_LABELS,
  VENDOR_TRADES,
  type Vendor,
  type VendorTrade,
} from '@/models/Vendor';
import { AUTO_PICK_CRITERIA } from '@/services/api/vendors';

// ─── TOKENS (kept local to avoid coupling to HomeownerHub's 7k-line file) ──
const C = {
  bg2: 'var(--tx-glass-panel)',
  bg3: 'var(--tx-glass-panel)',
  bg4: '#383838',
  lime: '#c0ff02',
  text: 'var(--tx-text)',
  muted: 'var(--tx-faint)',
  subtle: 'var(--tx-muted)',
  border: 'var(--tx-line)',
  green: '#4CAF79',
  yellow: '#FFF981',
};

const card = (extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: C.bg2,
  backgroundImage: 'var(--tx-surface-light)',
  boxShadow: 'var(--tx-shadow)',
  border: 0,
  borderRadius: '24px',
  padding: '20px',
  ...extra,
});

const fontStack = "'Manrope',sans-serif";

const URGENCY_LEVELS = [
  IssueUrgency.Critical,
  IssueUrgency.Urgent,
  IssueUrgency.Moderate,
];

const COUNTED_URGENCY_LEVELS = [
  IssueUrgency.Critical,
  IssueUrgency.Urgent,
  IssueUrgency.Moderate,
  IssueUrgency.Low,
  IssueUrgency.Monitor,
];

const URGENCY_COLORS: Record<IssueUrgency, string> = {
  [IssueUrgency.Critical]: '#DB5282',
  [IssueUrgency.Urgent]: '#FF9A4D',
  [IssueUrgency.Moderate]: '#FFF981',
  [IssueUrgency.Low]: '#5AE9FF',
  [IssueUrgency.Monitor]: '#8b8680',
};

type TradeIssueSignal = {
  count: number;
  urgencyCounts: Record<IssueUrgency, number>;
};

type DirectorySortKey = 'trade' | 'tasks' | 'saved';

const urgencyRank: Record<IssueUrgency, number> = {
  [IssueUrgency.Critical]: 0,
  [IssueUrgency.Urgent]: 1,
  [IssueUrgency.Moderate]: 2,
  [IssueUrgency.Low]: 3,
  [IssueUrgency.Monitor]: 4,
};

const fmtMoney = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value)
    ? `$${Math.round(value).toLocaleString()}`
    : null;

const fmtIssueRange = (issue: Issue) => {
  const min = fmtMoney(issue.costEstimateMin);
  const max = fmtMoney(issue.costEstimateMax);
  if (min && max) return min === max ? min : `${min} - ${max}`;
  return min ?? max ?? '-';
};

const sumIssueCosts = (issues: Issue[]) =>
  issues.reduce(
    (acc, issue) => ({
      min: acc.min + (issue.costEstimateMin ?? 0),
      max: acc.max + (issue.costEstimateMax ?? 0),
    }),
    { min: 0, max: 0 },
  );

const fmtCostRange = ({ min, max }: { min: number; max: number }) => {
  if (min <= 0 && max <= 0) return null;
  if (min === max) return fmtMoney(min);
  return `${fmtMoney(min) ?? '$0'} - ${fmtMoney(max) ?? '$0'}`;
};

const normalizeTradeSignal = (value?: string | null) =>
  (value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const issueTradeLookup = Object.entries(VENDOR_TRADE_LABELS).reduce<Record<string, VendorTrade>>(
  (acc, [trade, label]) => {
    acc[normalizeTradeSignal(trade)] = trade as VendorTrade;
    acc[normalizeTradeSignal(label)] = trade as VendorTrade;
    return acc;
  },
  {
    [normalizeTradeSignal('HVAC SYSTEMS')]: 'air_conditioning_and_heat',
    [normalizeTradeSignal('HVAC & Systems')]: 'air_conditioning_and_heat',
    [normalizeTradeSignal('HVAC')]: 'air_conditioning_and_heat',
    [normalizeTradeSignal('AC')]: 'air_conditioning_and_heat',
    [normalizeTradeSignal('Air Conditioning')]: 'air_conditioning_and_heat',
    [normalizeTradeSignal('EXTERIOR')]: 'general_contracting',
    [normalizeTradeSignal('INTERIOR')]: 'handyman',
    [normalizeTradeSignal('STRUCTURAL')]: 'structural',
    [normalizeTradeSignal('PLUMBING')]: 'plumbing',
    [normalizeTradeSignal('ELECTRICAL')]: 'electrical',
    [normalizeTradeSignal('POOL & SPA')]: 'pool_and_spa',
    [normalizeTradeSignal('POOL AND SPA')]: 'pool_and_spa',
    [normalizeTradeSignal('SEPTIC & SEWER')]: 'septic_and_sewer',
    [normalizeTradeSignal('MOLD')]: 'mold_and_water_damage',
    [normalizeTradeSignal('WATER DAMAGE')]: 'mold_and_water_damage',
  },
);

const inferIssueVendorTrade = (issue: Issue): VendorTrade | null => {
  const signals = [
    issue.trade,
    issue.componentCategory,
    issue.category,
    issue.component,
    issue.title,
    issue.description,
  ];
  for (const signal of signals) {
    const normalized = normalizeTradeSignal(signal);
    const direct = issueTradeLookup[normalized];
    if (direct) return direct;
    if (/hvac|aircondition|airconditioning|acunit|furnace|heatpump|condenser|compressor|duct|thermostat/.test(normalized)) {
      return 'air_conditioning_and_heat';
    }
    if (/mold|waterdamage|waterintrusion|watermitigation|moisture|remediation|restoration/.test(normalized)) {
      return 'mold_and_water_damage';
    }
    if (/electric|electrical|wiring|outlet|gfci|breaker|panel|grounding|bonding/.test(normalized)) {
      return 'electrical';
    }
    if (/plumb|pipe|drain|supplyline|sewer|waterheater|fixture|faucet|toilet/.test(normalized)) {
      return 'plumbing';
    }
    if (/roof|flashing|shingle|gutter|chimney|underlayment|tile/.test(normalized)) return 'roofing';
    if (/pool|spa|heater|pump/.test(normalized)) return 'pool_and_spa';
    if (/termite|pest|rodent|insect/.test(normalized)) return 'pest_control';
    if (/window|door|slider|glass|glazing/.test(normalized)) return 'windows_and_doors';
    if (/floor|carpet|tile|hardwood/.test(normalized)) return 'flooring';
    if (/paint|coating|finish/.test(normalized)) return 'painting';
    if (/foundation|framing|structural|settlement|retainingwall/.test(normalized)) return 'structural';
    if (/landscape|irrigation|sprinkler|drainage|grading/.test(normalized)) return 'landscaping';
    if (/appliance|oven|range|dishwasher|garbage disposal|microwave/.test(signal ?? '')) return 'appliances';
  }
  return null;
};

/**
 * Reactive mobile flag — mirrors the useWindowWidth hook used elsewhere
 * in HomeHQ. Previously a non-reactive function: it evaluated once and
 * never updated on resize, so mobile padding was missing whenever the
 * panel first rendered above 768px.
 */
const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

// the sub-pages close like Settings and the bid request page: the ds X, top-right, back to HomeHQ
function BackBtn() {
  const navigate = useNavigate();
  return (
    <CloseButton
      label="Close"
      onClose={() => navigate('/dashboard/home-hub')}
    />
  );
}

function SourceBadge({ source }: { source: Vendor['source'] }) {
  if (source === 'google_places') {
    return (
      <span
        title="Listed on Google"
        aria-label="Listed on Google"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
        <svg width="13" height="13" viewBox="0 0 48 48" aria-hidden="true">
          <path
            fill="#FBBC04"
            d="M9.83 24c0-1.52.25-2.98.7-4.36L2.62 13.6C1.08 16.73.2 20.26.2 24s.88 7.27 2.42 10.4l7.91-6.05c-.45-1.38-.7-2.83-.7-4.35z"
          />
          <path
            fill="#EA4335"
            d="M23.7 10.13c3.31 0 6.3 1.17 8.65 3.09L39.21 6.45C35.05 2.85 29.7.59 23.7.59c-9.36 0-17.4 5.35-21.08 13.01l7.91 6.04C12.33 14.04 17.55 10.13 23.7 10.13z"
          />
          <path
            fill="#34A853"
            d={[
              'M23.7 37.87c-6.15 0-11.37-3.91-13.17-9.52',
              'l-7.91 6.04c3.68 7.66 11.72 13.01 21.08 13.01',
              '5.74 0 11.22-2.04 15.34-5.86l-7.5-5.81',
              'c-2.13 1.34-4.83 2.14-7.84 2.14z',
            ].join(' ')}
          />
          <path
            fill="#4285F4"
            d="M46.98 24c0-1.36-.21-2.81-.53-4.17H23.7v8.85h13.09c-.65 3.22-2.45 5.69-5.04 7.39l7.5 5.81C43.6 37.7 46.98 31.45 46.98 24z"
          />
        </svg>
      </span>
    );
  }
  return null;
}


function TradeTasksDialog({
  trade,
  issues,
  onClose,
}: {
  trade: VendorTrade;
  issues: Issue[];
  onClose: () => void;
}) {
  const costRange = fmtCostRange(sumIssueCosts(issues));
  return (
    <Dialog
      data-testid="trade-tasks-dialog"
      open
      onClose={onClose}
      width={576}
      containerProps={{ p: '0px', gap: 0 }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: 'min(720px, calc(100dvh - 48px))',
            overflow: 'hidden',
            fontFamily: fontStack,
            backgroundColor: 'rgba(187,183,175,0.1) !important',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          },
        },
        backdrop: {
          sx: {
            backgroundColor: 'var(--tx-glass-panel)',
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
          },
        },
      }}
      header={
        <div
          style={{
            padding: '20px 52px 16px 20px',
            borderBottom: `1px solid ${C.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
              {VENDOR_TRADE_LABELS[trade]}
            </div>
            <div style={{ fontSize: 11, color: C.subtle, marginTop: 2 }}>
              {issues.length} {issues.length === 1 ? 'task' : 'tasks'}
              {costRange ? ` · ${costRange}` : ''}
            </div>
          </div>
        </div>
      }>
      <div
        style={{
          maxHeight: 'min(560px, calc(100dvh - 160px))',
          overflowY: 'auto',
          padding: '20px',
        }}>
        {issues.map((issue, index) => {
          const urgencyColor = URGENCY_COLORS[issue.urgency] ?? C.subtle;
          return (
            <div
              key={issue.id}
              style={{
                marginBottom: index < issues.length - 1 ? 5 : 0,
                background: 'rgba(var(--tx-ink-rgb), 0.05)',
                borderRadius: '12px',
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}>
              <span
                style={{
                  width: 3,
                  height: 28,
                  borderRadius: 2,
                  background: urgencyColor,
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, lineHeight: 1.25 }}>
                  {issue.title}
                </div>
                <div style={{ fontSize: 11, color: C.subtle, marginTop: 4, lineHeight: 1.25 }}>
                  {issue.category || issue.trade || VENDOR_TRADE_LABELS[trade]}
                </div>
              </div>
              <div
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 5,
                }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.text,
                    whiteSpace: 'nowrap',
                  }}>
                  {fmtIssueRange(issue)}
                </span>
                <span
                  style={{
                    color: urgencyColor,
                    background: `${urgencyColor}22`,
                    borderRadius: 6,
                    padding: '3px 8px',
                    fontSize: 10,
                    fontWeight: 600,
                    lineHeight: 1,
                  }}>
                  {issue.urgency}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}

const PhoneIcon = (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <path
      d="M3.5 1.5h2l1.3 3.2-1.7 1c.9 1.8 2.4 3.3 4.2 4.2l1-1.7 3.2 1.3v2c0 .6-.4 1-1 1A11 11 0 0 1 2.5 2.5c0-.6.4-1 1-1Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const MapIcon = (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 1.5C5.5 1.5 3.5 3.5 3.5 6c0 3 4.5 8 4.5 8s4.5-5 4.5-8c0-2.5-2-4.5-4.5-4.5Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <circle cx="8" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

const MailIcon = (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="3.5" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
    <path d="M2.5 4.5L8 9l5.5-4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);



// The directory, as a phone directory: compact, hairline-divided, hundreds of contacts at a glance.
// A trade is a thin header line (its task mix as small ticks, how many pros are saved, one quiet action);
// each saved pro is a single row (name, rating, distance, number) whose actions appear on hover.
// one column template for the header and every trade line: name | tasks (urgency hashes) | saved | action
const DIR_COLS = 'minmax(0, 1fr) 72px 64px 124px';
const DIR_COLS_MOBILE = 'minmax(0, 1fr) 30px 28px auto';

const DIRECTORY_CSS = `
.hx-dir-row { transition: background 0.12s ease; }
.hx-dir-row:hover { background: var(--tx-fill); }
.hx-dir-row .hx-dir-actions { opacity: 0; transition: opacity 0.12s ease; }
.hx-dir-row:hover .hx-dir-actions, .hx-dir-row:focus-within .hx-dir-actions { opacity: 1; }
.hx-dir-act { transition: background 0.12s ease, color 0.12s ease; }
.hx-dir-act:hover { background: rgba(var(--tx-ink-rgb), 0.1); color: var(--tx-text); }
.hx-dir-find { transition: color 0.12s ease, opacity 0.12s ease; }
.hx-dir-find:hover { color: var(--tx-text); }
.hx-dir-trade .hx-dir-find { opacity: 0; }
.hx-dir-trade:hover .hx-dir-find, .hx-dir-trade:focus-within .hx-dir-find { opacity: 1; }
@media (hover: none) { .hx-dir-trade .hx-dir-find { opacity: 1; } }
@media (hover: none) { .hx-dir-row .hx-dir-actions { opacity: 1; } }
`;

function ContactRow({ vendor, isMobile, onRemove }: { vendor: Vendor; isMobile: boolean; onRemove: (id: string) => void }) {
  const mapsHref = vendor.googleMapsUri
    ? vendor.googleMapsUri
    : vendor.place_id
      ? `https://www.google.com/maps/place/?q=place_id:${vendor.place_id}`
      : vendor.address
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendor.address)}`
        : null;
  const isManual = vendor.source === 'manual';
  const meta = [
    !isManual && vendor.rating != null ? `★ ${vendor.rating.toFixed(1)}` : null,
    !isManual && vendor.reviewCount ? `${vendor.reviewCount} reviews` : null,
    vendor.distance ? `${vendor.distance.value.toFixed(1)} ${vendor.distance.unit}` : null,
    vendor.priceLevel != null ? '$'.repeat(Math.max(1, Math.min(4, vendor.priceLevel))) : null,
  ].filter(Boolean);
  const initials = vendor.name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  const act: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 28,
    padding: '0 10px',
    borderRadius: 8,
    border: 0,
    background: 'transparent',
    color: C.muted,
    fontFamily: fontStack,
    fontSize: 12,
    fontWeight: 500,
    textDecoration: 'none',
    cursor: 'pointer',
  };
  return (
    <div
      data-testid={`vendor-row-${vendor.id}`}
      className="hx-dir-row"
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '32px minmax(0, 1fr) auto' : '32px minmax(0, 1.4fr) minmax(0, 1fr) 140px auto',
        alignItems: 'center',
        gap: 14,
        minHeight: 52,
        padding: '6px 10px',
        borderRadius: 10,
        fontFamily: fontStack,
      }}>
      <span
        aria-hidden
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--tx-fill-2)',
          color: C.muted,
          fontSize: 11,
          fontWeight: 600,
        }}>
        {initials}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: C.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {vendor.name}
          </span>
          <SourceBadge source={vendor.source} />
        </div>
        {(isMobile || !vendor.address) && meta.length > 0 && (
          <div style={{ fontSize: 12, fontWeight: 500, color: C.muted, marginTop: 2 }}>{meta.join(' · ')}</div>
        )}
        {!isMobile && vendor.address && meta.length > 0 && (
          <div style={{ fontSize: 12, fontWeight: 500, color: C.muted, marginTop: 2 }}>{meta.join(' · ')}</div>
        )}
      </div>
      {!isMobile && (
        <span style={{ fontSize: 12, color: C.subtle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {vendor.address ?? ''}
        </span>
      )}
      {!isMobile && (
        <span style={{ fontSize: 13, color: C.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {vendor.phone ?? ''}
        </span>
      )}
      <div className="hx-dir-actions" style={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
        {vendor.phone && (
          <a href={`tel:${vendor.phone}`} aria-label={`Call ${vendor.phone}`} className="hx-dir-act" style={act}>
            {PhoneIcon}
            {!isMobile && 'Call'}
          </a>
        )}
        {vendor.email && (
          <a href={`mailto:${vendor.email}`} aria-label={`Email ${vendor.email}`} className="hx-dir-act" style={act}>
            {MailIcon}
          </a>
        )}
        {mapsHref && (
          <a href={mapsHref} target="_blank" rel="noreferrer" aria-label="Open in Google Maps" className="hx-dir-act" style={act}>
            {MapIcon}
          </a>
        )}
        <button type="button" onClick={() => onRemove(vendor.id)} className="hx-dir-act" style={act}>
          Remove
        </button>
      </div>
    </div>
  );
}

function DirectorySection({
  trade,
  vendors,
  issueSignal,
  maxCount,
  isMobile,
  onFind,
  onOpenIssues,
  onRemove,
}: {
  trade: VendorTrade;
  vendors: Vendor[];
  issueSignal?: TradeIssueSignal;
  /** the highest single-urgency count across all trades, so hash lengths compare down the list */
  maxCount: number;
  isMobile: boolean;
  onFind: (trade: VendorTrade) => void;
  onOpenIssues: (trade: VendorTrade) => void;
  onRemove: (id: string) => void;
}) {
  const isEmpty = vendors.length === 0;
  const total = issueSignal?.count ?? 0;
  return (
    <section data-testid={`trade-section-${trade}`} className="hx-dir-trade" style={{ fontFamily: fontStack }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? DIR_COLS_MOBILE : DIR_COLS,
          alignItems: 'center',
          columnGap: 12,
          minHeight: 44,
          padding: '0 10px',
          borderBottom: '1px solid var(--tx-line)',
        }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 500,
            letterSpacing: '-0.01em',
            color: C.text,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {VENDOR_TRADE_LABELS[trade]}
        </span>
        {/* the task mix as thin hashes (critical, urgent, moderate), their lengths comparable across every trade */}
        <button
          type="button"
          onClick={() => total > 0 && onOpenIssues(trade)}
          disabled={total === 0}
          aria-label={`${total} ${total === 1 ? 'task' : 'tasks'} mapped to this trade`}
          title={total ? `${total} ${total === 1 ? 'task' : 'tasks'}` : undefined}
          style={{
            justifySelf: 'end',
            display: 'flex',
            alignItems: 'flex-end',
            gap: 5,
            height: 18,
            padding: 0,
            border: 0,
            background: 'transparent',
            cursor: total > 0 ? 'pointer' : 'default',
          }}>
          {URGENCY_LEVELS.map((u) => {
            const n = issueSignal?.urgencyCounts[u] ?? 0;
            return (
              <span
                key={u}
                style={{
                  width: 2,
                  height: n === 0 ? 2 : Math.max(4, (n / Math.max(1, maxCount)) * 18),
                  borderRadius: 1,
                  background: n === 0 ? 'var(--tx-fill-2)' : URGENCY_COLORS[u],
                }}
              />
            );
          })}
        </button>
        <span style={{ fontSize: 12, fontWeight: 500, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: isEmpty ? C.subtle : C.text }}>
          {isEmpty ? '—' : vendors.length}
        </span>
        <span style={{ position: 'relative', justifySelf: 'end', display: 'grid', placeItems: 'end' }}>
        <button
          type="button"
          className="hx-dir-find"
          onClick={() => onFind(trade)}
          style={{
            justifySelf: 'end',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: 0,
            border: 0,
            background: 'transparent',
            color: C.muted,
            fontFamily: fontStack,
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}>
          <span style={{ fontSize: 14, lineHeight: 1 }}>+</span>
          {isEmpty ? 'Find vendors' : 'Add'}
        </button>
        </span>
      </div>
      {!isEmpty && (
        <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0 8px 18px' }}>
          {vendors.map((v) => (
            <ContactRow key={v.id} vendor={v} isMobile={isMobile} onRemove={onRemove} />
          ))}
        </div>
      )}
    </section>
  );
}

function AutoPickTip({ children }: { children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            background: 'var(--tx-glass)',
            boxShadow: 'var(--tx-shadow-sm)',
            backdropFilter: 'var(--tx-blur)',
            WebkitBackdropFilter: 'var(--tx-blur)',
            borderRadius: '9px',
            padding: '8px 12px',
            fontSize: 11,
            color: 'var(--tx-text)',
            fontFamily: fontStack,
            fontWeight: 400,
            lineHeight: 1.5,
            pointerEvents: 'none',
            zIndex: 9999,
            width: 240,
            textAlign: 'left',
          }}>
          <div style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>
            Curates up to 3 per trade that meet:
          </div>
          <div>
            ★ {AUTO_PICK_CRITERIA.minRating.toFixed(1)}+ rating
            <span style={{ opacity: 0.5, margin: '0 6px' }}>·</span>
            {AUTO_PICK_CRITERIA.minReviewCount}+ reviews
          </div>
        </div>
      )}
    </div>
  );
}

export function VendorDirectoryPanel() {
  const m = useIsMobile();
  const { selectedProperty } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const { isAdvisor, canAccessAdmin } = useUserRole();
  const { groups, isLoading: isLoadingGroups } = useClientGroups();
  const { conditionScore } = useHauserEQResults(propertyId);
  const { vendors, isFetchingVendors, deleteVendor, autoPopulateAll, isAutoPopulatingAll } =
    useVendors(propertyId);
  const { issues = [] } = useGetIssuesForProperty(propertyId ?? '');
  const [search, setSearch] = useState('');
  const [findingTrade, setFindingTrade] = useState<VendorTrade | null>(null);
  const [taskDialogTrade, setTaskDialogTrade] = useState<VendorTrade | null>(null);
  const [sortKey, setSortKey] = useState<DirectorySortKey>('tasks');
  // trades with nothing flagged and nobody saved fold into one line per group until asked for
  const [showQuiet, setShowQuiet] = useState<Set<string>>(new Set());
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [confirmAutoPickOpen, setConfirmAutoPickOpen] = useState(false);
  const [reportNeededOpen, setReportNeededOpen] = useState(false);
  const [autoPickError, setAutoPickError] = useState<string | null>(null);
  const [shiftX, setShiftX] = useState(0);
  const isSelfServeOwner =
    !isAdvisor &&
    !canAccessAdmin &&
    !isLoadingGroups &&
    groups.length === 0 &&
    selectedProperty?.isOwned === true;
  const smartVendorDiscoveryLocked = isSelfServeOwner && conditionScore == null;
  useEffect(() => {
    if (!confirmAutoPickOpen) return;
    const measure = () => {
      const main = document.getElementById('dashboard-main-content');
      if (main) {
        const r = main.getBoundingClientRect();
        setShiftX(r.left + r.width / 2 - window.innerWidth / 2);
      }
    };
    measure();
    const main = document.getElementById('dashboard-main-content');
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [confirmAutoPickOpen]);

  const runAutoPopulateAll = async () => {
    setAutoPickError(null);
    try {
      await autoPopulateAll(3);
      setConfirmAutoPickOpen(false);
    } catch (error) {
      console.error('Failed to curate vendors for all trades:', error);
      setAutoPickError('Could not curate vendors. Try again.');
    }
  };

  const grouped = useMemo(() => {
    const map = new Map<VendorTrade, Vendor[]>();
    VENDOR_TRADES.forEach((t) => map.set(t, []));
    vendors
      .filter((v) => v.pinned)
      .filter((v) => !search || v.name.toLowerCase().includes(search.toLowerCase()))
      .forEach((v) => map.get(v.trade)?.push(v));
    return map;
  }, [vendors, search]);


  const totalPinned = vendors.filter((v) => v.pinned).length;
  const issuesByTrade = useMemo(() => {
    const map = new Map<VendorTrade, Issue[]>();
    for (const issue of issues) {
      const trade = inferIssueVendorTrade(issue);
      if (!trade) continue;
      const tradeIssues = map.get(trade) ?? [];
      tradeIssues.push(issue);
      map.set(trade, tradeIssues);
    }
    for (const tradeIssues of map.values()) {
      tradeIssues.sort((a, b) => {
        const urgencyDiff = urgencyRank[a.urgency] - urgencyRank[b.urgency];
        if (urgencyDiff !== 0) return urgencyDiff;
        return a.title.localeCompare(b.title);
      });
    }
    return map;
  }, [issues]);

  const issueSignalsByTrade = useMemo(() => {
    const map = new Map<VendorTrade, TradeIssueSignal>();
    for (const [trade, tradeIssues] of issuesByTrade.entries()) {
      const signal =
        map.get(trade) ??
        ({
          count: 0,
          urgencyCounts: Object.fromEntries(
            COUNTED_URGENCY_LEVELS.map((urgency) => [urgency, 0]),
          ) as Record<IssueUrgency, number>,
        });
      for (const issue of tradeIssues) {
        signal.count += 1;
        signal.urgencyCounts[issue.urgency] += 1;
      }
      map.set(trade, signal);
    }
    return map;
  }, [issuesByTrade]);

  const maxUrgencyCount = useMemo(
    () =>
      Math.max(
        1,
        ...[...issueSignalsByTrade.values()].flatMap((sig) => URGENCY_LEVELS.map((u) => sig.urgencyCounts[u])),
      ),
    [issueSignalsByTrade],
  );

  const taskDialogIssues = taskDialogTrade ? issuesByTrade.get(taskDialogTrade) ?? [] : [];


  /**
   * Sort happens within each group so the Real Estate & Closing section
   * doesn't get mixed into the service providers. Groups themselves
   * stay in canonical order.
   */
  const sortedGroups = useMemo(
    () =>
      VENDOR_TRADE_GROUPS.map((g) => ({
        label: g.label,
        trades: [...g.trades].sort((a, b) => {
          const dir = sortDir === 'asc' ? 1 : -1;
          const byName = VENDOR_TRADE_LABELS[a].localeCompare(VENDOR_TRADE_LABELS[b]);
          if (sortKey === 'trade') return byName * dir;
          if (sortKey === 'saved') {
            const diff = (grouped.get(a)?.length ?? 0) - (grouped.get(b)?.length ?? 0);
            return diff ? diff * dir : byName;
          }
          // tasks: most urgent first (critical, then urgent, then moderate), then total
          const sa = issueSignalsByTrade.get(a);
          const sb = issueSignalsByTrade.get(b);
          for (const u of URGENCY_LEVELS) {
            const diff = (sa?.urgencyCounts[u] ?? 0) - (sb?.urgencyCounts[u] ?? 0);
            if (diff) return diff * dir;
          }
          const totalDiff = (sa?.count ?? 0) - (sb?.count ?? 0);
          return totalDiff ? totalDiff * dir : byName;
        }),
      })),
    [issueSignalsByTrade, sortDir, sortKey, grouped],
  );


  return (
    <div
      style={{
        // full width, as every page: no 1200px cap
        paddingTop: m ? 0 : 20,
        paddingBottom: 20,
        paddingLeft: m ? 10 : 0,
        paddingRight: m ? 10 : 0,
        fontFamily: fontStack,
      }}>
      <div
        style={{
          ...card(),
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: m ? 'calc(100dvh - 104px)' : 'calc(100dvh - 40px)',
        }}>
        {/* Header — title row gets full width so subcopy can breathe;
            action buttons drop to a second row below. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            padding: '20px 20px 16px',
            flexShrink: 0,
          }}>
          {/* Row 1: title + back */}
          <div style={{ display: 'flex', alignItems: 'center', gap: m ? 10 : 14 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 300, color: C.text }}>Directory</div>
            </div>
            <BackBtn />
          </div>

          {/* Row 2: action buttons (desktop also gets the search input
              inline at the left of this row; mobile keeps search on its
              own row below to avoid cramming). */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: m ? 8 : 10,
              justifyContent: m ? 'flex-end' : 'flex-start',
              flexWrap: 'wrap',
            }}>
            {!m && (
              <div
                style={{
                  flex: 1,
                  minWidth: 180,
                  height: 40,
                  background: 'var(--tx-fill)',
                  border: 0,
                  borderRadius: '12px',
                  padding: '0 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  boxSizing: 'border-box',
                }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <circle cx="7" cy="7" r="4.5" stroke={C.muted} strokeWidth="1.3" />
                  <path d="M11 11l3 3" stroke={C.muted} strokeWidth="1.3" strokeLinecap="round" />
                </svg>
                <input
                  data-testid="vendor-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search saved vendors…"
                  style={{
                    background: 'none',
                    border: 'none',
                    outline: 'none',
                    fontSize: 13,
                    color: C.text,
                    fontFamily: fontStack,
                    flex: 1,
                    minWidth: 0,
                  }}
                />
              </div>
            )}

          {/* Auto-pick across all trades */}
          <AutoPickTip>
            <button
              data-testid="vendor-directory-auto-pick-all"
              onClick={() => {
                setAutoPickError(null);
                if (smartVendorDiscoveryLocked) {
                  setReportNeededOpen(true);
                  return;
                }
                setConfirmAutoPickOpen(true);
              }}
              disabled={isAutoPopulatingAll}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: C.lime,
                border: 'none',
                borderRadius: 10,
                height: 40,
                padding: '0 14px',
                fontSize: 13,
                fontWeight: 600,
                color: '#111',
                cursor: isAutoPopulatingAll ? 'not-allowed' : 'pointer',
                opacity: isAutoPopulatingAll ? 0.6 : 1,
                fontFamily: fontStack,
                whiteSpace: 'nowrap',
              }}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="#111" aria-hidden="true">
                <path d="M9.5 1.2 3 9h3.7L6 14.8 13 7H9.3l.2-5.8Z" />
              </svg>
              {m ? 'Curate' : 'Curate all'}
            </button>
          </AutoPickTip>

          {/* Sort */}

          </div>
        </div>

        {/* Mobile-only search bar on its own row */}
        {m && (
          <div style={{ padding: '0 20px 16px', flexShrink: 0 }}>
            <div
              style={{
                height: 40,
                background: 'var(--tx-fill)',
                border: 0,
                borderRadius: '12px',
                padding: '0 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                boxSizing: 'border-box',
              }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <circle cx="7" cy="7" r="4.5" stroke={C.muted} strokeWidth="1.3" />
                <path d="M11 11l3 3" stroke={C.muted} strokeWidth="1.3" strokeLinecap="round" />
              </svg>
              <input
                data-testid="vendor-search-mobile"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search saved vendors…"
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  fontSize: 13,
                  color: C.text,
                  fontFamily: fontStack,
                  flex: 1,
                  minWidth: 0,
                }}
              />
            </div>
          </div>
        )}

        {/* Body: the list sits in a darker inset pocket (as the Records viewer), so the controls above and the
            directory itself read as two things */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', padding: m ? '0 10px 10px' : '4px 20px 20px' }}>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            scrollbarWidth: 'none',
            borderRadius: 20,
            background: '#262626',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
            padding: m ? '10px 8px 16px' : '16px 18px 22px',
          }}>
          {isFetchingVendors && (
            <div
              style={{
                padding: 20,
                textAlign: 'center',
                color: C.muted,
                fontSize: 13,
              }}>
              Loading…
            </div>
          )}

          {!isFetchingVendors && totalPinned === 0 && !search && (
            <div
              data-testid="vendor-empty-state"
              style={{
                padding: m ? '20px 20px 40px' : '40px 20px',
                textAlign: 'center',
                color: C.subtle,
              }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 8 }}>
                No vendors saved yet
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, maxWidth: 360, margin: '0 auto' }}>
                Save 2–3 trusted vendors per trade, so you're covered before something breaks.
              </div>
            </div>
          )}

          {!isFetchingVendors && <style>{DIRECTORY_CSS}</style>}
          {!isFetchingVendors && (() => {
            const sortBy = (key: DirectorySortKey) => {
              if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
              else {
                setSortKey(key);
                setSortDir(key === 'trade' ? 'asc' : 'desc');
              }
            };
            const head = (key: DirectorySortKey | null, label: string, align: 'left' | 'right' = 'right') => (
              <button
                type="button"
                disabled={!key}
                onClick={() => key && sortBy(key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                  gap: 4,
                  padding: 0,
                  border: 0,
                  background: 'transparent',
                  color: key && sortKey === key ? C.text : C.subtle,
                  fontFamily: fontStack,
                  fontSize: 11,
                  fontWeight: 500,
                  cursor: key ? 'pointer' : 'default',
                  whiteSpace: 'nowrap',
                }}>
                {label}
                {key && <SortCaret active={sortKey === key} dir={sortDir} />}
              </button>
            );
            return (
              <div>
              <div
                style={{
                  // a quiet column head: small labels over a hairline, no band
                  display: 'grid',
                  gridTemplateColumns: m ? DIR_COLS_MOBILE : DIR_COLS,
                  alignItems: 'end',
                  columnGap: 12,
                  height: 30,
                  padding: '0 10px 8px',
                  borderBottom: '1px solid var(--tx-line)',
                }}>
                {head('trade', 'Trade', 'left')}
                {head('tasks', 'Tasks')}
                {head('saved', 'Saved')}
                <span />
              </div>
              </div>
            );
          })()}
          {!isFetchingVendors &&
            sortedGroups.map((group, gi) => (
              <div key={group.label} style={{ marginTop: gi === 0 ? 0 : 32 }}>
                {gi > 0 && (
                  <div style={{ fontSize: 11, fontWeight: 500, color: C.subtle, padding: '0 10px 6px' }}>{group.label}</div>
                )}
                {(() => {
                  const matters = (t: VendorTrade) =>
                    (issueSignalsByTrade.get(t)?.count ?? 0) > 0 || (grouped.get(t)?.length ?? 0) > 0;
                  const quiet = group.trades.filter((t) => !matters(t));
                  const shown = showQuiet.has(group.label) || search ? group.trades : group.trades.filter(matters);
                  return (
                    <>
                {shown.map((trade) => (
                  <DirectorySection
                    key={trade}
                    trade={trade}
                    vendors={grouped.get(trade) ?? []}
                    issueSignal={issueSignalsByTrade.get(trade)}
                    maxCount={maxUrgencyCount}
                    isMobile={m}
                    onFind={(t) => setFindingTrade(t)}
                    onOpenIssues={(t) => setTaskDialogTrade(t)}
                    onRemove={(id) => deleteVendor(id)}
                  />
                ))}
                {quiet.length > 0 && !search && (
                  <button
                    type="button"
                    className="hx-dir-find"
                    onClick={() =>
                      setShowQuiet((prev) => {
                        const next = new Set(prev);
                        if (next.has(group.label)) next.delete(group.label);
                        else next.add(group.label);
                        return next;
                      })
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      minHeight: 40,
                      padding: '0 10px',
                      border: 0,
                      background: 'transparent',
                      color: C.muted,
                      fontFamily: fontStack,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}>
                    {showQuiet.has(group.label)
                      ? `Hide ${quiet.length} quiet ${quiet.length === 1 ? 'trade' : 'trades'}`
                      : `${quiet.length} more ${quiet.length === 1 ? 'trade' : 'trades'} with nothing flagged`}
                    <span style={{ transform: showQuiet.has(group.label) ? 'rotate(180deg)' : 'none' }}>⌄</span>
                  </button>
                )}
                    </>
                  );
                })()}
              </div>
            ))}
        </div>
        </div>
      </div>

      {findingTrade && propertyId && (
        <VendorFinderDialog
          trade={findingTrade}
          propertyId={propertyId}
          savedVendors={vendors}
          smartDiscoveryLocked={smartVendorDiscoveryLocked}
          onClose={() => setFindingTrade(null)}
        />
      )}

      {reportNeededOpen && (
        <MuiDialog
          open
          onClose={() => setReportNeededOpen(false)}
          maxWidth={false}
          slotProps={{
            paper: {
              sx: {
                background: C.bg2,
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                border: 0,
                borderRadius: '24px',
                width: '100%',
                maxWidth: 430,
                padding: '24px',
                fontFamily: fontStack,
              },
            },
            backdrop: {
              sx: {
                backgroundColor: 'var(--tx-backdrop)',
                backdropFilter: 'blur(6px)',
              },
            },
          }}>
          <div style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: C.text, marginBottom: 8 }}>
            Upload a report to unlock vendor recommendations
          </div>
          <div style={{ fontSize: 13, color: C.subtle, lineHeight: 1.6, marginBottom: 18 }}>
            We’ll use your inspection report to map repair needs to the right trades, vendors, and
            RFQs. Manual vendor entry is still available from each trade.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setReportNeededOpen(false)}
              style={{
                background: C.lime,
                border: 'none',
                color: '#111',
                borderRadius: 8,
                padding: '8px 14px',
                cursor: 'pointer',
                fontFamily: fontStack,
                fontSize: 12,
                fontWeight: 600,
              }}>
              Got it
            </button>
          </div>
        </MuiDialog>
      )}

      {taskDialogTrade && taskDialogIssues.length > 0 && (
        <TradeTasksDialog
          trade={taskDialogTrade}
          issues={taskDialogIssues}
          onClose={() => setTaskDialogTrade(null)}
        />
      )}

      {confirmAutoPickOpen && (
        <MuiDialog
          data-testid="auto-pick-confirm"
          open
          onClose={() => setConfirmAutoPickOpen(false)}
          maxWidth={false}
          slotProps={{
            paper: {
              sx: {
                background: C.bg2,
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                border: 0,
                borderRadius: '24px',
                width: '100%',
                maxWidth: 440,
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                fontFamily: fontStack,
                transform: `translateX(${shiftX}px)`,
              },
            },
          }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              Curate vendors for all trades?
            </div>
            <div style={{ fontSize: 13, color: C.subtle, lineHeight: 1.6 }}>
              We'll add up to 3 vendors per trade that meet:
            </div>
            <div
              style={{
                fontSize: 12,
                color: C.text,
                background: 'var(--tx-fill)',
                border: 0,
                borderRadius: '12px',
                padding: '10px 12px',
                lineHeight: 1.7,
                margin: '20px 0',
              }}>
              <div>
                <span style={{ color: 'rgba(192,255,2,0.8)', fontWeight: 600 }}>★</span>{' '}
                {AUTO_PICK_CRITERIA.minRating.toFixed(1)}+ rating
                <span style={{ opacity: 0.4, margin: '0 6px' }}>·</span>
                {AUTO_PICK_CRITERIA.minReviewCount}+ reviews
              </div>
            </div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
              You can add, edit, or remove any vendor later. Trades with vendors already saved are
              skipped.
            </div>
            {isAutoPopulatingAll && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  color: C.subtle,
                  fontSize: 12,
                  marginTop: 16,
                }}>
                <CircularProgress size={18} thickness={3.5} sx={{ color: C.lime }} />
                Curating vendors across trades...
              </div>
            )}
            {autoPickError && (
              <div style={{ color: '#DB5282', fontSize: 12, marginTop: 16 }}>{autoPickError}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
              <button
                onClick={() => setConfirmAutoPickOpen(false)}
                disabled={isAutoPopulatingAll}
                style={{
                  background: 'var(--tx-fill)',
                  border: 0,
                  color: C.subtle,
                  borderRadius: 8,
                  padding: '8px 14px',
                  cursor: isAutoPopulatingAll ? 'not-allowed' : 'pointer',
                  fontFamily: fontStack,
                  fontSize: 12,
                  fontWeight: 600,
                  opacity: isAutoPopulatingAll ? 0.55 : 1,
                }}>
                Cancel
              </button>
              <button
                data-testid="auto-pick-confirm-go"
                onClick={runAutoPopulateAll}
                disabled={isAutoPopulatingAll}
                style={{
                  background: C.lime,
                  border: 'none',
                  color: '#111',
                  borderRadius: 8,
                  padding: '8px 14px',
                  cursor: isAutoPopulatingAll ? 'not-allowed' : 'pointer',
                  fontFamily: fontStack,
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: isAutoPopulatingAll ? 0.75 : 1,
                }}>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="#111" aria-hidden="true">
                  <path d="M9.5 1.2 3 9h3.7L6 14.8 13 7H9.3l.2-5.8Z" />
                </svg>
                Curate
              </button>
            </div>
          </div>
        </MuiDialog>
      )}
    </div>
  );
}

export default VendorDirectoryPanel;
